import type { Id } from '@odhaczka/core';
import { AutoScroller, defaultPreset, PointerActivationConstraints } from '@dnd-kit/dom';
import { move } from '@dnd-kit/helpers';
import { DragDropProvider, KeyboardSensor, PointerSensor, useDroppable } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import { useCallback, useRef, useState } from 'react';
import type { ListItemRow } from '../../db/types';
import { IconGrip } from './icons';
import { ItemText } from './ItemText';

interface Props {
  /** „Gdzieś po drodze”: produkty bez miejsca w sklepie — tylko źródło przeciągania. */
  unknown: ListItemRow[];
  /** Trasa: produkty z miejscem, w kolejności sklepu — tu się upuszcza. */
  route: ListItemRow[];
  onMove: (productId: Id, afterId: Id | null, beforeId: Id | null) => void;
}

type Groups = { unknown: Id[]; route: Id[] };

const ROUTE = 'route';
const UNKNOWN = 'unknown';
// Niski priorytet kolizji: pozycje trasy wygrywają z samą strefą (ona liczy się, gdy trasa jest pusta).
const COLLISION_LOW = 1;

// Uchwyt ma touch-action: none, więc przewijanie z niego nie startuje — przeciąganie bez opóźnienia.
const sensors = [
  PointerSensor.configure({
    activationConstraints: (event) =>
      event.pointerType === 'mouse' ? undefined : [new PointerActivationConstraints.Distance({ value: 3 })],
  }),
  KeyboardSensor,
];

// Auto-przewijanie dopiero przy samej krawędzi (8% wysokości ≈ pod przyklejonym „Gotowe”) i wolniej:
// domyślne 20% łapało palec nad zwykłymi wierszami i lista uciekała spod upuszczenia.
const plugins = defaultPreset.plugins.map((p) =>
  p === AutoScroller ? AutoScroller.configure({ threshold: { x: 0, y: 0.08 }, acceleration: 12 }) : p,
);

const idsOf = (unknown: ListItemRow[], route: ListItemRow[]): Groups => ({
  unknown: unknown.map((i) => i.id),
  route: route.map((i) => i.id),
});

/**
 * Tryb przestawiania w dwóch grupach. Produkt z „gdzieś po drodze” przeciąga się na trasę,
 * produkty na trasie przestawia się między sobą; powrót nad „po drodze” anuluje przeciąganie.
 * Grupy przesuwamy w stanie już w trakcie przeciągania (onDragOver + move) — wtedy React sam
 * przenosi wiersz między listami, a dnd-kit nie rusza DOM-u.
 */
export function ReorderList({ unknown, route, onMove }: Props) {
  const signature = [...unknown, null, ...route].map((i) => i?.id ?? '|').join(',');
  const [synced, setSynced] = useState(signature);
  const [groups, setGroups] = useState<Groups>(() => idsOf(unknown, route));
  // Handlery dnd-kit biegną w startTransition — najświeższy stan trzymamy w refie.
  const latest = useRef(groups);
  const snapshot = useRef(groups);
  // „Po drodze” zamrożone na czas przeciągania: wyciągnięty wiersz zostawia ducha tej samej wysokości,
  // inaczej lista pod palcem podskoczyłaby o wiersz i trasa uciekła spod upuszczenia.
  const [frozen, setFrozen] = useState<Id[] | null>(null);
  if (synced !== signature) {
    const fresh = idsOf(unknown, route);
    setSynced(signature);
    setGroups(fresh);
    latest.current = fresh;
  }
  const apply = (next: Groups) => {
    latest.current = next;
    setGroups(next);
  };

  const byId = new Map([...unknown, ...route].map((i) => [i.id, i]));
  const rowsOf = (ids: Id[]) => ids.map((id) => byId.get(id)).filter((i): i is ListItemRow => i !== undefined);
  const unknownShown = rowsOf(frozen ?? groups.unknown);
  // Do „po drodze” można wrócić tylko tym, co stamtąd wyszło — to anuluje przeciąganie.
  const cameFromUnknown = useCallback((draggable: { id: string | number }) => snapshot.current.unknown.includes(String(draggable.id)), []);

  return (
    <DragDropProvider
      sensors={sensors}
      plugins={plugins}
      onDragStart={() => {
        snapshot.current = latest.current;
        setFrozen(latest.current.unknown);
      }}
      onDragOver={(event) => {
        apply(move(latest.current, event));
      }}
      onDragEnd={(event) => {
        setFrozen(null);
        const before = snapshot.current;
        const { source } = event.operation;
        if (event.canceled || !source) return apply(before);
        // Liczy się to, gdzie wiersz stoi na ekranie (upuszczenie obok wraca na ostatnie miejsce na trasie).
        const id = String(source.id);
        const now = latest.current.route;
        const at = now.indexOf(id);
        if (at === -1) return apply(before);
        if (before.route.indexOf(id) === at && before.route.length === now.length) return;
        const productId = byId.get(id)?.productId;
        if (!productId) return apply(before);
        const pid = (k: number) => byId.get(now[k] ?? '')?.productId ?? null;
        onMove(productId, at > 0 ? pid(at - 1) : null, pid(at + 1));
      }}
    >
      {unknownShown.length > 0 && (
        <UnknownZone accept={cameFromUnknown}>
          <SectionTitle title="Gdzieś po drodze" hint="Przeciągnij na trasę, w miejsce, gdzie leży." />
          <ul>
            {unknownShown.map((item) => {
              const index = groups.unknown.indexOf(item.id);
              return index === -1 ? <GhostRow key={item.id} item={item} /> : <ReorderRow key={item.id} item={item} index={index} group={UNKNOWN} />;
            })}
          </ul>
        </UnknownZone>
      )}
      {/* Zapas pod trasą poza strefą upuszczania: koniec listy da się wyciągnąć nad pasek „Gotowe”. */}
      <section className="pb-24">
        <SectionTitle title="Trasa" />
        <RouteZone empty={groups.route.length === 0}>
          {rowsOf(groups.route).map((item, index) => (
            <ReorderRow key={item.id} item={item} index={index} group={ROUTE} />
          ))}
        </RouteZone>
      </section>
    </DragDropProvider>
  );
}

/** Sekcja „po drodze” jako strefa powrotu: upuszczenie tutaj zostawia produkt bez miejsca. */
function UnknownZone({ accept, children }: { accept: (draggable: { id: string | number }) => boolean; children: React.ReactNode }) {
  const { ref } = useDroppable({ id: UNKNOWN, type: 'column', accept, collisionPriority: COLLISION_LOW });
  return <section ref={ref}>{children}</section>;
}

/** Puste miejsce po wierszu wyciągniętym z „po drodze” (tylko w trakcie przeciągania). */
function GhostRow({ item }: { item: ListItemRow }) {
  return (
    <li aria-hidden className="flex min-h-14 items-center gap-2 border-b border-stone-200 bg-stone-100 pl-4 opacity-40 dark:border-stone-800 dark:bg-stone-900">
      <ItemText item={item} />
      <span className="size-14 shrink-0" />
    </li>
  );
}

function SectionTitle({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="px-4 pt-4 pb-1.5">
      <h2 className="text-sm font-semibold tracking-wide text-stone-500 uppercase">{title}</h2>
      {hint && <p className="mt-0.5 text-sm text-stone-500">{hint}</p>}
    </div>
  );
}

/** Strefa upuszczania trasy — potrzebna, żeby dało się upuścić pierwszy produkt na pustą trasę. */
function RouteZone({ empty, children }: { empty: boolean; children: React.ReactNode }) {
  const { ref, isDropTarget } = useDroppable({ id: ROUTE, type: 'column', accept: 'item', collisionPriority: COLLISION_LOW });
  return (
    <ul ref={ref} className={`min-h-16 ${isDropTarget && empty ? 'bg-brand-50 dark:bg-brand-800/20' : ''}`}>
      {children}
      {empty && (
        <li className="m-3 rounded-xl border-2 border-dashed border-stone-300 p-4 text-center text-sm text-stone-500 dark:border-stone-700">
          Przeciągnij tu produkt, który leży najbliżej wejścia — od niego zacznie się trasa.
        </li>
      )}
    </ul>
  );
}

function ReorderRow({ item, index, group }: { item: ListItemRow; index: number; group: typeof UNKNOWN | typeof ROUTE }) {
  const movable = item.productId !== null;
  const { ref, handleRef, isDragSource } = useSortable({
    id: item.id,
    index,
    group,
    type: 'item',
    accept: 'item',
    // „Po drodze” to tylko źródło: nie da się tam niczego upuścić ani przestawić.
    disabled: { draggable: !movable, droppable: group !== ROUTE },
  });
  return (
    <li
      ref={ref}
      className={`relative flex min-h-14 items-center gap-2 border-b border-stone-200 bg-white pl-4 dark:border-stone-800 dark:bg-stone-900 ${
        isDragSource ? 'z-10 shadow-lg ring-2 ring-brand-600' : ''
      }`}
    >
      <ItemText item={item} />
      {movable ? (
        <button
          ref={handleRef}
          type="button"
          aria-label={`Przeciągnij: ${item.name}`}
          style={{ touchAction: 'none' }}
          className="flex size-14 shrink-0 cursor-grab items-center justify-center text-stone-400 active:cursor-grabbing active:text-brand-700"
        >
          <IconGrip />
        </button>
      ) : (
        <span className="size-14 shrink-0" />
      )}
    </li>
  );
}
